import { isAfter, parseISO } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import type { FormikErrors } from 'formik';

import { CP_VALIDATION_ERRORS } from '../constants';
import type {
  CPScheduleDateRange,
  DateRangeValidationError,
  ScheduleFormErrors,
  SetCPScheduleFormValues,
} from '../types';
import { CpScheduleFormFields } from '../types';

const validateRequiredFields = (
  dateRange: CPScheduleDateRange,
  isDynamicStartDate: boolean,
  isDynamicEndDate: boolean
): DateRangeValidationError => {
  const errors: DateRangeValidationError = {};

  if (!dateRange.start && !isDynamicStartDate) {
    errors.start = CP_VALIDATION_ERRORS.SCHEDULE_START_IS_REQUIRED;
  }

  if (!dateRange.end && !isDynamicEndDate) {
    errors.end = CP_VALIDATION_ERRORS.SCHEDULE_END_IS_REQUIRED;
  }

  return errors;
};

const validateHeavyUp = (heavyUp: number | null): Pick<DateRangeValidationError, 'heavyUp'> => {
  if (heavyUp === null) {
    return { heavyUp: CP_VALIDATION_ERRORS.HEAVY_UP_IS_REQUIRED };
  }
  if (heavyUp < 100) {
    return { heavyUp: CP_VALIDATION_ERRORS.HEAVY_UP_GREATER_THAN };
  }

  return {};
};

const validateDateInPast = (date: Date, nowInTz: Date, isDynamic: boolean): boolean => {
  return date <= nowInTz && !isDynamic;
};

const validateDateRangeOrder = (
  startDate: Date,
  endDate: Date,
  isDynamicStartDate: boolean,
  isDynamicEndDate: boolean
): boolean => {
  return startDate >= endDate && !isDynamicEndDate && !isDynamicStartDate;
};

const validateDateTiming = (
  dateRange: CPScheduleDateRange,
  timezone: string,
  isDynamicStartDate: boolean,
  isDynamicEndDate: boolean
): DateRangeValidationError => {
  const errors: DateRangeValidationError = {};

  if (!dateRange.start || !dateRange.end) {
    return errors;
  }

  const startDate = parseISO(dateRange.start);
  const endDate = parseISO(dateRange.end);
  const nowInTz = utcToZonedTime(new Date(), timezone);

  const isStartInPast = validateDateInPast(startDate, nowInTz, isDynamicStartDate);
  const isEndInPast = validateDateInPast(endDate, nowInTz, isDynamicEndDate);

  if (isStartInPast) {
    errors.start = CP_VALIDATION_ERRORS.SCHEDULE_START_IN_THE_FUTURE;
  }

  if (isEndInPast) {
    errors.end = CP_VALIDATION_ERRORS.SCHEDULE_END_IN_THE_FUTURE;
  }

  if (
    !isStartInPast &&
    !isEndInPast &&
    validateDateRangeOrder(startDate, endDate, isDynamicStartDate, isDynamicEndDate)
  ) {
    errors.end = CP_VALIDATION_ERRORS.SCHEDULE_START_BEFORE_END;
  }

  return errors;
};

const validateScheduleDateRange = (
  dateRange: CPScheduleDateRange,
  timezone: string,
  isDynamicStartDate: boolean,
  isDynamicEndDate: boolean
): DateRangeValidationError => {
  const requiredFieldErrors = validateRequiredFields(dateRange, isDynamicStartDate, isDynamicEndDate);
  const heavyUpErrors = validateHeavyUp(dateRange.heavyUp);
  const dateTimingErrors = validateDateTiming(dateRange, timezone, isDynamicStartDate, isDynamicEndDate);

  return {
    ...requiredFieldErrors,
    ...heavyUpErrors,
    ...dateTimingErrors,
  };
};

const updateRangesErrorsWithOrderValidationErrors = (
  rangeErrors: FormikErrors<CPScheduleDateRange>[],
  ranges: CPScheduleDateRange[],
  isDynamicStartDateEnabled: boolean
): FormikErrors<CPScheduleDateRange>[] => {
  const updatedRangeErrors = [...rangeErrors];

  for (let index = 0; index < ranges.length - 1; index++) {
    const currentRange = ranges[index];
    const nextRange = ranges[index + 1];

    if (nextRange.start) {
      const nextStart = parseISO(nextRange.start);
      let hasScheduleOrderError: boolean = false;
      let hasScheduleOverlapError: boolean = false;

      if (currentRange.start) {
        const currentStart = parseISO(currentRange.start);
        hasScheduleOrderError = isAfter(currentStart, nextStart) && !(index === 0 && isDynamicStartDateEnabled);
      }

      if (currentRange.end) {
        const currentEnd = parseISO(currentRange.end);
        hasScheduleOverlapError = isAfter(currentEnd, nextStart);
      }

      if (hasScheduleOrderError) {
        updatedRangeErrors[index + 1] = {
          ...updatedRangeErrors[index + 1],
          start: CP_VALIDATION_ERRORS.SCHEDULE_ORDER_ERROR,
        };
      } else if (hasScheduleOverlapError) {
        updatedRangeErrors[index + 1] = {
          ...updatedRangeErrors[index + 1],
          start: CP_VALIDATION_ERRORS.SCHEDULE_CANT_OVERLAP,
        };
      }
    }
  }

  return updatedRangeErrors;
};

export const validateScheduleForm = (values: SetCPScheduleFormValues): ScheduleFormErrors => {
  const errors: ScheduleFormErrors = {};
  const timezone = values[CpScheduleFormFields.Timezone];

  if (!timezone) {
    errors[CpScheduleFormFields.Timezone] = CP_VALIDATION_ERRORS.TIMEZONE_IS_REQUIRED;
  }

  const isDynamicStartDateEnabled = values[CpScheduleFormFields.IsDynamicStartDateEnabled];
  const isDynamicEndDateEnabled = values[CpScheduleFormFields.IsDynamicEndDateEnabled];

  const ranges = values[CpScheduleFormFields.Ranges];
  const rangeErrors: FormikErrors<CPScheduleDateRange>[] = ranges.map((range, index) =>
    validateScheduleDateRange(
      range,
      timezone,
      index === 0 && isDynamicStartDateEnabled,
      index === ranges.length - 1 && isDynamicEndDateEnabled
    )
  );
  const updatedRangeErrors = updateRangesErrorsWithOrderValidationErrors(
    rangeErrors,
    ranges,
    isDynamicStartDateEnabled
  );

  const hasRangeErrors = updatedRangeErrors.some((error) => Object.keys(error).length > 0);

  if (hasRangeErrors) {
    errors[CpScheduleFormFields.Ranges] = updatedRangeErrors;
  }

  return errors;
};
